import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import axios from "axios";
import { API_URL } from "config";
import SoftSelect from "components/SoftSelect";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const AddSubBranch = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [formData, setFormData] = useState({
    branch_id: "",
    name: "",
    address: "",
    status: true,
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
    icon: "check",
  });

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasSubBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasSubBranchPermission("sub_branch.pages.create.view");
  const canSubmit =
    hasSubBranchPermission("sub_branch.actions.create") &&
    hasSubBranchPermission("sub_branch.actions.submit_create");
  const canOpenList =
    hasSubBranchPermission("sub_branch.pages.list.view") &&
    hasSubBranchPermission("sub_branch.actions.open_list");
  const canOpenTrashed =
    hasSubBranchPermission("sub_branch.pages.trashed.view") &&
    hasSubBranchPermission("sub_branch.actions.open_trashed");
  const canViewField = useCallback(
    (field) => hasSubBranchPermission(`sub_branch.fields.${field}.view`),
    [hasSubBranchPermission]
  );

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage || !canViewField("branch")) {
      setPageLoading(false);
      return;
    }

    const fetchBranches = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`${API_URL}/sub-branches/branch-options`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
        setBranches(res.data?.data || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to load branches",
          color: "error",
          icon: "error",
        });
      } finally {
        setPageLoading(false);
      }
    };

    fetchBranches();
  }, [canViewField, canViewPage, isAuthReady]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to create sub branches.",
        color: "error",
        icon: "error",
      });
      return;
    }

    setLoading(true);

    if (!formData.branch_id || !formData.name) {
      setSnackbar({
        open: true,
        message: "Parent branch and sub-branch name are required.",
        color: "error",
        icon: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_URL}/sub-branches`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          branch_id: formData.branch_id,
          name: formData.name,
          address: formData.address,
          status: formData.status,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create sub-branch.");
      }

      setSnackbar({
        open: true,
        message: "Sub-branch created successfully!",
        color: "success",
        icon: "check",
      });
      setTimeout(() => navigate("/sub-branches"), 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to create sub-branch.",
        color: "error",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Add Sub Branch
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to add sub branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const branchOptions = branches.map((branch) => ({
    value: branch.id,
    label: branch.name,
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4">Add Sub Branch</SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenList && (
              <SoftButton color="info" onClick={() => navigate("/sub-branches")} startIcon={<Icon>list</Icon>}>
                Sub Branch List
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                color="dark"
                variant="outlined"
                onClick={() => navigate("/sub-branches/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trashed List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox maxWidth="600px" ml="0">
          <form onSubmit={handleSubmit}>
            {canViewField("branch") && (
              <SoftBox mb={2}>
                <SoftSelect
                  size="small"
                  placeholder="Select Parent Branch"
                  options={branchOptions}
                  value={branchOptions.find((option) => option.value === formData.branch_id) || null}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      branch_id: option ? option.value : "",
                    }))
                  }
                />
              </SoftBox>
            )}

            {canViewField("name") && (
              <SoftBox mb={2}>
                <SoftInput
                  size="small"
                  name="name"
                  placeholder="Sub Branch Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </SoftBox>
            )}

            {canViewField("address") && (
              <SoftBox mb={2}>
                <SoftInput
                  size="small"
                  name="address"
                  placeholder="Address"
                  value={formData.address}
                  onChange={handleChange}
                />
              </SoftBox>
            )}

            {canViewField("status") && (
              <SoftBox mb={2} display="flex" alignItems="center">
                <Switch checked={formData.status} onChange={handleChange} name="status" />
                <SoftTypography variant="button" ml={1}>
                  {formData.status ? "Active" : "Inactive"}
                </SoftTypography>
              </SoftBox>
            )}

            {canSubmit && (
              <SoftBox mt={3}>
                <SoftButton
                  size="small"
                  type="submit"
                  color="info"
                  fullWidth
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? "Processing..." : "Create Sub Branch"}
                </SoftButton>
              </SoftBox>
            )}
          </form>
        </SoftBox>
      </SoftBox>

      <SoftSnackbar
        color={snackbar.color}
        icon={snackbar.icon || "notifications"}
        title="Sub Branch"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
      />
    </DashboardLayout>
  );
};

export default AddSubBranch;
